import { ApiError } from '@greencandle/api-client'

/**
 * The terminal's shared vocabulary: the four ways a figure can render, and the two frames it
 * renders inside.
 *
 * <p>Extracted from `TerminalPage.tsx` so that the rule these encode can be tested. That rule is
 * the codebase's central one and it is easy to lose by accident: a figure the server could not
 * produce renders as an explicit gap **carrying the server's own reason**, never as a zero and
 * never as a bare dash. `Gap` is not decoration - it is the difference between "this account
 * holds no property" and "the property could not be valued", and a screen that blurs the two is
 * lying quietly.
 */

export type QueryLike = { isPending: boolean; isError: boolean; error: unknown }

/** A figure the API could not produce, with the reason it gave and the story that would fix it. */
export function Gap({ story, text }: { story?: string; text: string }) {
  return (
    <>
      <div className="value gap-value">not available</div>
      <div className="sub">
        {story && <span className="story-pill">{story}</span>} {text}
      </div>
    </>
  )
}

/** A whole panel the mock shows and no endpoint can answer. */
export function PanelGap({
  stories,
  children,
}: {
  stories: ReadonlyArray<readonly [string, string]>
  children: React.ReactNode
}) {
  return (
    <div className="panel-gap">
      <span className="badge badge-custom">not built</span>
      <p>{children}</p>
      <ul>
        {stories.map(([id, text]) => (
          <li key={id}>
            <span className="story-pill">{id}</span> {text}
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * In flight, or failed.
 *
 * <p>An error takes precedence over the spinner: a query that has failed is not still loading,
 * and rendering an ellipsis forever is how a dead endpoint passes for a slow one.
 */
export function Pending({ query }: { query: QueryLike }) {
  if (query.isError) return <Failed error={query.error} />
  return <div className="value faint">…</div>
}

/**
 * A failed request, saying what the server said.
 *
 * <p>`problem.detail` is RFC 7807's human-readable sentence and is written to be shown. The
 * fallback is deliberately vague only because a non-ApiError here means the request never
 * reached the API at all.
 */
export function Failed({ error }: { error: unknown }) {
  return (
    <div className="notice error" role="alert">
      {error instanceof ApiError
        ? (error.problem.detail ?? error.message)
        : 'Could not reach the API.'}
    </div>
  )
}

export function StatCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="stat-card">
      <div className="label">{label}</div>
      {children}
    </div>
  )
}

/** Two decimal places, or an em dash. Never a zero standing in for an absent figure. */
export function num(value: string | null | undefined): string {
  if (value == null) return '—'
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed.toFixed(2) : '—'
}

/** A ratio as a percentage. Same absence rule as {@link num}. */
export function pct(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${(value * 100).toFixed(2)}%`
}

/** '', 'up' or 'down' - the class that colours a signed figure. Zero is neither. */
export function toneOf(value: string | null | undefined): string {
  if (value == null) return ''
  const n = Number(value)
  if (!Number.isFinite(n) || n === 0) return ''
  return n > 0 ? 'up' : 'down'
}

export function daysUntil(date: string): number | null {
  const target = Date.parse(date)
  if (Number.isNaN(target)) return null
  return Math.round((target - Date.now()) / 86_400_000)
}
